import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format, addDays, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { api } from "@/lib/hkApi";
import { toast } from "sonner";
import {
  BedDouble, ChevronLeft, ChevronRight, Sparkles, RefreshCw,
  LogIn, LogOut, Home, CheckCircle2, Circle, Refrigerator, Coffee, ChevronDown, ChevronUp, Pencil, Layers,
  Key, Eye, EyeOff, Receipt,
} from "lucide-react";

const STATUS_META = {
  partenza: { label: "Partenza", cls: "bg-[#4A1C17] text-[#F7EBDF]", icon: LogOut },
  arrivo: { label: "Arrivo", cls: "bg-[#7C5A4E] text-[#F7EBDF]", icon: LogIn },
  in_casa: { label: "In casa", cls: "bg-[#FAF3EE] text-[#4A1C17] border border-[#E8DCC8]", icon: Home },
  libera: { label: "Libera", cls: "bg-white text-[#7C5A4E] border border-[#E8DCC8]", icon: BedDouble },
};

const TaskRow = ({ room, task, onToggle }) => (
  <button
    type="button"
    data-testid={`task-check-${room.id}-${task.id}`}
    onClick={() => onToggle(room.id, task.id)}
    className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-xl border transition-all ${
      task.done
        ? "bg-[#E6F4EA] border-[#CEEAD6]"
        : task.auto
          ? "bg-[#FBF6E8] border-[#C5A059]/50 hover:border-[#C5A059]"
          : "bg-white border-[#E8DCC8] hover:border-[#4A1C17]/40"
    }`}
  >
    {task.done
      ? <CheckCircle2 className="h-5 w-5 text-[#137333] shrink-0" />
      : <Circle className={`h-5 w-5 shrink-0 ${task.auto ? "text-[#C5A059]" : "text-[#7C5A4E]/50"}`} />}
    <span className={`text-sm font-medium flex-1 ${task.done ? "line-through text-[#7C5A4E]" : "text-[#2B1410]"}`}>
      {task.label}
    </span>
    {task.auto && !task.done && <Sparkles className="h-4 w-4 text-[#C5A059] shrink-0" />}
  </button>
);

const FRIGO_ACTIONS = [
  { field: "mancante", label: "Mancante", active: "bg-amber-600 text-white border-amber-600" },
  { field: "consumato", label: "Consumato", active: "bg-[#4A1C17] text-[#F7EBDF] border-[#4A1C17]" },
  { field: "rifornito", label: "Rifornito", active: "bg-[#137333] text-white border-[#137333]" },
];

const FrigobarSection = ({ room, onMark, section = "frigobar", title = "Frigo bar", icon: Icon = Refrigerator, testKey = "frigo" }) => {
  const [open, setOpen] = useState(false);
  const items = (room.frigobar || []).filter((i) => (i.section || "frigobar") === section);
  if (items.length === 0) return null;
  const marked = items.filter((i) => (i.consumato || 0) > 0 || (i.rifornito || 0) > 0 || (i.mancante || 0) > 0).length;
  return (
    <div className="px-4 sm:px-5 pb-4 sm:pb-5">
      <button
        type="button"
        data-testid={`${testKey}-toggle-${room.id}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E8DCC8] bg-[#FAF3EE] hover:border-[#4A1C17]/40 transition-all"
      >
        <span className="h-8 w-8 rounded-lg bg-[#4A1C17] text-[#F7EBDF] flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-sm font-semibold text-[#2B1410] flex-1 text-left">
          {title} {marked > 0 && <span className="text-[#7C5A4E] font-normal">· {marked} segnalati</span>}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-[#7C5A4E]" /> : <ChevronDown className="h-4 w-4 text-[#7C5A4E]" />}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`${testKey}-list-${room.id}`}>
          {items.map((item) => {
            const incomplete = item.presente < item.dotazione;
            const isMulti = (item.dotazione || 1) > 1;
            return (
              <div key={item.id} data-testid={`${testKey}-item-${room.id}-${item.id}`} className={`px-3 py-2 rounded-xl border ${incomplete ? "bg-[#FBF6E8] border-[#C5A059]/50" : "border-[#E8DCC8] bg-white"}`}>
                <div className="flex items-center gap-2">
                  {item.image_url && (
                    <img src={item.image_url} alt={item.name} data-testid={`${testKey}-img-${room.id}-${item.id}`} className="h-9 w-9 rounded-lg object-cover border border-[#E8DCC8] shrink-0 bg-white" />
                  )}
                  <span className="text-sm flex-1 min-w-0 text-[#2B1410]">
                    {item.name}
                    {item.price && <span className="text-xs text-[#7C5A4E] ml-1.5">{item.price}</span>}
                  </span>
                  <span data-testid={`${testKey}-presente-${room.id}-${item.id}`} className={`text-xs font-semibold shrink-0 ${incomplete ? "text-amber-700" : "text-[#137333]"}`}>
                    In camera: {item.presente}/{item.dotazione}
                  </span>
                </div>
                <div className="flex gap-1.5 mt-1.5">
                  {FRIGO_ACTIONS.map((a) => {
                    const qty = item[a.field] || 0;
                    const activeStyle = qty > 0;
                    return (
                      <button
                        key={a.field}
                        type="button"
                        data-testid={`${testKey}-${a.field}-${room.id}-${item.id}`}
                        onClick={() => onMark(room.id, item.id, a.field)}
                        className={`flex-1 rounded-full px-2 py-1 text-[11px] font-semibold transition-all border ${
                          activeStyle ? a.active : "bg-white text-[#7C5A4E] border-[#E8DCC8] hover:border-[#4A1C17]/40"
                        }`}
                      >
                        {a.label}{isMulti && qty > 0 ? ` · ${qty}` : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const GuestLine = ({ testId, icon: Icon, prefix, name, resId, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(name || "");
  const save = async () => {
    if (!resId) return;
    try {
      await api.put(`/reservations/${resId}`, { summary: val.trim() || "Prenotazione" });
      setEditing(false);
      onSaved();
    } catch { toast.error("Errore salvataggio nome"); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-xs font-semibold text-[#7C5A4E] shrink-0">{prefix}</span>
        <input
          autoFocus
          data-testid={`${testId}-input`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="Nome ospite…"
          className="flex-1 min-w-0 rounded-full border border-[#E8DCC8] bg-white px-3 py-1 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
        />
        <button data-testid={`${testId}-save`} onClick={save} className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-3 py-1 text-xs font-semibold transition-colors shrink-0">Salva</button>
      </div>
    );
  }
  return (
    <button type="button" data-testid={testId} onClick={() => { setVal(name || ""); setEditing(true); }} className="flex items-center gap-1.5 mt-1 text-left group">
      <Icon className="h-3.5 w-3.5 text-[#7C5A4E] shrink-0" />
      <span className="text-sm font-medium text-[#2B1410]">
        {prefix} {name || <span className="italic text-[#7C5A4E]">nome da definire</span>}
      </span>
      <Pencil className="h-3 w-3 text-[#C5A059] shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

const PinRow = ({ resId, pin }) => {
  const [show, setShow] = useState(false);
  if (!resId) return null;
  return (
    <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`pin-row-${resId}`}>
      <Key className="h-3.5 w-3.5 text-[#C5A059] shrink-0" />
      {pin ? (
        <span className="inline-flex items-center gap-1 font-mono font-bold text-[#4A1C17]">
          {show ? pin : "••••••"}
          <button onClick={() => setShow((v) => !v)} className="text-[#7C5A4E] hover:text-[#4A1C17]">
            {show ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          </button>
        </span>
      ) : (
        <span className="italic text-[#7C5A4E]">codice non generato</span>
      )}
      <span className="text-[10.5px] text-[#7C5A4E] italic">· gestisci da Admin → Serrature</span>
    </div>
  );
};

const LINEN_ACTIONS = [
  { field: "tolto", label: "Tolto (sporco)", active: "bg-amber-600 text-white border-amber-600" },
  { field: "messo", label: "Messo pulito", active: "bg-[#137333] text-white border-[#137333]" },
];

const LinenSection = ({ room, onMark }) => {
  const [open, setOpen] = useState(false);
  const total = room.biancheria.reduce((s, i) => s + i.presente, 0);
  const segnalati = room.biancheria.filter((i) => i.tolto || i.messo).length;
  return (
    <div className="px-4 sm:px-5 pb-4 sm:pb-5">
      <button
        type="button"
        data-testid={`linen-toggle-${room.id}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E8DCC8] bg-[#FAF3EE] hover:border-[#4A1C17]/40 transition-all"
      >
        <span className="h-8 w-8 rounded-lg bg-[#7C5A4E] text-[#F7EBDF] flex items-center justify-center shrink-0">
          <Layers className="h-4 w-4" />
        </span>
        <span className="text-sm font-semibold text-[#2B1410] flex-1 text-left">
          Biancheria
          <span className="text-[#7C5A4E] font-normal">
            {total > 0 && ` · ${total} pezzi in camera`}
            {segnalati > 0 && ` · ${segnalati} segnalati oggi`}
          </span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-[#7C5A4E]" /> : <ChevronDown className="h-4 w-4 text-[#7C5A4E]" />}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`linen-list-${room.id}`}>
          <p className="text-[11px] text-[#7C5A4E] px-1">"Tolto" = sporco verso la lavanderia · "Messo pulito" = preso dal magazzino</p>
          {room.biancheria.map((item) => (
            <div key={item.id} data-testid={`linen-item-${room.id}-${item.id}`} className={`px-3 py-2 rounded-xl border ${item.presente === 0 ? "bg-[#FBF6E8] border-[#C5A059]/50" : "border-[#E8DCC8] bg-white"}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm flex-1 min-w-0 text-[#2B1410]">{item.name}</span>
                <span data-testid={`linen-presente-${room.id}-${item.id}`} className={`text-xs font-semibold shrink-0 ${item.presente === 0 ? "text-amber-700" : "text-[#137333]"}`}>
                  In camera: {item.presente}
                </span>
              </div>
              <div className="flex gap-1.5 mt-1.5">
                {LINEN_ACTIONS.map((a) => (
                  <button
                    key={a.field}
                    type="button"
                    data-testid={`linen-${a.field}-${room.id}-${item.id}`}
                    onClick={() => onMark(room.id, item.id, a.field)}
                    className={`flex-1 rounded-full px-2 py-1 text-[11px] font-semibold transition-all border ${
                      item[a.field] ? a.active : "bg-white text-[#7C5A4E] border-[#E8DCC8] hover:border-[#4A1C17]/40"
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ROOM_LOGOS = ["argo", "bestla", "clizio", "damaseno", "efialte", "fenja", "idas"];

const RoomCard = ({ room, onToggle, onMark, onGuestSaved, onLinenMark, index }) => {
  const meta = STATUS_META[room.status];
  const Icon = meta.icon;
  const pct = room.total ? Math.round((room.done / room.total) * 100) : 0;
  const complete = room.total > 0 && room.done === room.total;
  return (
    <div
      data-testid={`room-card-${room.id}`}
      className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-sm overflow-hidden"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="p-4 sm:p-5 flex items-start gap-4">
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${complete ? "bg-[#137333] text-white" : "bg-[#4A1C17] text-[#F7EBDF]"}`}>
          {complete ? <CheckCircle2 className="h-6 w-6" /> : room.slug && ROOM_LOGOS.includes(room.slug) ? (
            <img src={`/media/room-logo-${room.slug}.png`} alt={room.name} className="h-11 w-11 object-contain" data-testid={`room-logo-${room.id}`} />
          ) : <BedDouble className="h-6 w-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg font-bold text-[#2B1410]">
              {room.number ? `${room.number}. ` : ""}{room.name}
            </h3>
            <span data-testid={`room-status-${room.id}`} className={`rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1 ${meta.cls}`}>
              <Icon className="h-3 w-3" /> {meta.label}
            </span>
            {room.arrival_too && (
              <span className="rounded-full px-3 py-1 text-xs font-semibold bg-[#7C5A4E] text-[#F7EBDF] flex items-center gap-1">
                <LogIn className="h-3 w-3" /> + Arrivo
              </span>
            )}
          </div>
          <p className="text-sm text-[#7C5A4E] mt-1">
            {room.mode_label || "Nessun lavoro previsto"}
            {room.status === "in_casa" && room.day_number ? ` · Giorno ${room.day_number}` : ""}
          </p>
          {room.status === "in_casa" && (
            <>
              <GuestLine testId={`guest-stay-${room.id}`} icon={Home} prefix="In camera:" name={room.guest} resId={room.reservation_id} onSaved={onGuestSaved} />
              <PinRow resId={room.reservation_id} pin={room.ttlock_pin} phone={room.guest_phone} onSaved={onGuestSaved} />
            </>
          )}
          {room.status === "partenza" && (
            <>
              <GuestLine testId={`guest-dep-${room.id}`} icon={LogOut} prefix="Esce oggi:" name={room.guest} resId={room.reservation_id} onSaved={onGuestSaved} />
              <PinRow resId={room.reservation_id} pin={room.ttlock_pin} phone={room.guest_phone} onSaved={onGuestSaved} />
              <Link
                to={`/admin/pulizie/reception?room_id=${room.id}${room.stay_from ? `&from=${room.stay_from}` : ""}${room.stay_to ? `&to=${room.stay_to}` : ""}`}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#4A1C17] hover:bg-[#2b1410] text-[#F7EBDF] px-3 py-1.5 text-xs font-semibold transition-colors"
                data-testid={`frigobar-bill-${room.id}`}
              >
                <Receipt className="h-3.5 w-3.5" /> Conto frigobar
              </Link>
            </>
          )}
          {(room.status === "arrivo" || room.arrival_too) && (
            <>
              <GuestLine testId={`guest-arr-${room.id}`} icon={LogIn} prefix="Arriva oggi:" name={room.arrival_too ? room.next_guest : room.guest} resId={room.arrival_too ? room.next_reservation_id : room.reservation_id} onSaved={onGuestSaved} />
              <PinRow
                resId={room.arrival_too ? room.next_reservation_id : room.reservation_id}
                pin={room.arrival_too ? room.next_ttlock_pin : room.ttlock_pin}
                phone={room.arrival_too ? room.next_guest_phone : room.guest_phone}
                onSaved={onGuestSaved} />
            </>
          )}
          {room.status === "libera" && (
            <p className="text-xs text-[#7C5A4E] italic mt-0.5" data-testid={`free-label-${room.id}`}>Libera · nessun ospite e nessun arrivo</p>
          )}
          {room.total > 0 && (
            <div className="mt-3 flex items-center gap-3">
              <div className="h-2 flex-1 rounded-full bg-[#FAF3EE] border border-[#E8DCC8] overflow-hidden">
                <div
                  data-testid={`room-progress-${room.id}`}
                  className={`h-full rounded-full transition-all duration-500 ${complete ? "bg-[#137333]" : "bg-[#C5A059]"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-[#7C5A4E] shrink-0">{room.done}/{room.total}</span>
            </div>
          )}
        </div>
      </div>
      {room.total > 0 && (
        <div className="px-4 sm:px-5 pb-4 space-y-2">
          {room.tasks.map((t) => <TaskRow key={t.id} room={room} task={t} onToggle={onToggle} />)}
        </div>
      )}
      {room.frigobar && room.frigobar.length > 0 && (
        <>
          <FrigobarSection room={room} onMark={onMark} section="frigobar" title="Frigo bar" icon={Refrigerator} testKey="frigo" />
          <FrigobarSection room={room} onMark={onMark} section="caffetteria" title="Caffetteria in Camera" icon={Coffee} testKey="caffet" />
        </>
      )}
      {room.biancheria && room.biancheria.length > 0 && <LinenSection room={room} onMark={onLinenMark} />}
    </div>
  );
};

export default function Today() {
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(() => searchParams.get("date") || format(new Date(), "yyyy-MM-dd"));
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/day", { params: { date } });
      setData(res.data);
    } catch {
      toast.error("Errore nel caricamento dei dati", {
        description: "Prova a ricaricare l'app (svuota la cache automaticamente)",
        action: {
          label: "Ricarica app",
          onClick: async () => {
            // Nuke every possible browser cache so a stale build can't lock
            // the housekeeper out — SW registrations, cache storage, then
            // hard reload with a cache-busting query param.
            try {
              if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
              }
              if (navigator.serviceWorker) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
              }
            } catch (_) { /* silent */ }
            const url = new URL(window.location.href);
            url.searchParams.set("_refresh", Date.now().toString());
            window.location.replace(url.toString());
          },
        },
        duration: 15000,
      });
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const shift = (n) => setDate(format(addDays(parseISO(date), n), "yyyy-MM-dd"));

  const toggle = async (roomId, taskId) => {
    setData((prev) => ({
      ...prev,
      rooms: prev.rooms.map((r) => r.id !== roomId ? r : {
        ...r,
        tasks: r.tasks.map((t) => t.id === taskId ? { ...t, done: !t.done } : t),
        done: r.tasks.some((t) => t.id === taskId) ? r.done + (r.tasks.find((t) => t.id === taskId).done ? -1 : 1) : r.done,
      }),
    }));
    try {
      await api.post("/checks/toggle", { room_id: roomId, task_id: taskId, date });
    } catch {
      toast.error("Errore nel salvataggio");
      load();
    }
  };

  const markFrigo = async (roomId, itemId, field) => {
    // Compute new qty up-front from CURRENT state. For dotazione === 1 we
    // toggle 0 ↔ 1; for dotazione > 1 we cycle 0 → 1 → 2 → … → dotazione → 0.
    const currentItem = data?.rooms
      .find((r) => r.id === roomId)?.frigobar
      .find((i) => i.id === itemId);
    if (!currentItem) return;
    const dot = Math.max(1, currentItem.dotazione || 1);
    const cur = Number(currentItem[field] || 0);
    const newValue = cur >= dot ? 0 : cur + 1;
    setData((prev) => ({
      ...prev,
      rooms: prev.rooms.map((r) => r.id !== roomId ? r : {
        ...r,
        frigobar: r.frigobar.map((i) => {
          if (i.id !== itemId) return i;
          let presente = i.presente;
          const delta = newValue - cur;
          if (field === "consumato" || field === "mancante") presente = Math.max(0, presente - delta);
          if (field === "rifornito") presente = Math.max(0, presente + delta);
          return { ...i, [field]: newValue, presente };
        }),
      }),
    }));
    try {
      await api.post("/frigobar/mark", { room_id: roomId, item_id: itemId, date, field, value: newValue });
    } catch {
      toast.error("Errore nel salvataggio frigo bar");
      load();
    }
  };

  const markLinen = async (roomId, itemId, field) => {
    const currentItem = data?.rooms
      .find((r) => r.id === roomId)?.biancheria
      .find((i) => i.id === itemId);
    if (!currentItem) return;
    const newValue = !currentItem[field];
    setData((prev) => ({
      ...prev,
      rooms: prev.rooms.map((r) => r.id !== roomId ? r : {
        ...r,
        biancheria: r.biancheria.map((i) => {
          if (i.id !== itemId) return i;
          let presente = i.presente;
          if (field === "tolto") presente = Math.max(0, presente + (newValue ? -1 : 1));
          if (field === "messo") presente = presente + (newValue ? 1 : -1);
          return { ...i, [field]: newValue, presente };
        }),
      }),
    }));
    try {
      await api.post("/linen/mark", { room_id: roomId, item_id: itemId, date, field, value: newValue });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento biancheria");
      load();
    }
  };

  const syncPlanner = async () => {
    setSyncing(true);
    try {
      const startRes = await api.post("/sync-planner");
      if (startRes.data?.status === "already-running") {
        toast.info("Sincronizzazione già in corso, attendi il termine…");
      } else {
        toast.info("Sincronizzazione avviata in background…");
      }
      // Poll status every 3s up to 3 minutes.
      const deadline = Date.now() + 3 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const s = await api.get("/planner-status");
          if (!s.data?.running) {
            if (s.data?.last_error) {
              toast.error(`Sincronizzazione fallita: ${s.data.last_error}`);
            } else if (s.data?.last_result) {
              const r = s.data.last_result;
              toast.success(`Planner sincronizzato: ${r.imported ?? "?"} prenotazioni`);
            }
            await load();
            return;
          }
        } catch (_) { /* keep polling */ }
      }
      toast.warning("Sincronizzazione in corso oltre 3 minuti — ricarica manualmente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sincronizzazione planner fallita");
    } finally {
      setSyncing(false);
    }
  };

  const counts = data ? {
    partenze: data.rooms.filter((r) => r.status === "partenza").length,
    arrivi: data.rooms.filter((r) => r.status === "arrivo" || r.arrival_too).length,
    inCasa: data.rooms.filter((r) => r.status === "in_casa").length,
  } : null;

  return (
    <div data-testid="today-page">
      <div className="max-w-2xl mx-auto px-4 mt-4">
        <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 flex items-center gap-2">
          <button data-testid="date-prev-btn" onClick={() => shift(-1)} className="h-9 w-9 rounded-full border border-[#E8DCC8] flex items-center justify-center text-[#4A1C17] hover:bg-[#FAF3EE] transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 text-center">
            <p className="font-display text-lg font-bold text-[#2B1410] capitalize" data-testid="current-date-label">
              {format(parseISO(date), "EEEE d MMMM yyyy", { locale: it })}
            </p>
          </div>
          <button data-testid="date-next-btn" onClick={() => shift(1)} className="h-9 w-9 rounded-full border border-[#E8DCC8] flex items-center justify-center text-[#4A1C17] hover:bg-[#FAF3EE] transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            data-testid="today-btn"
            onClick={() => setDate(format(new Date(), "yyyy-MM-dd"))}
            className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-4 py-2 text-xs font-semibold transition-colors"
          >
            Oggi
          </button>
        </div>

        {counts && (
          <div className="flex items-center gap-2 mt-4 flex-wrap" data-testid="day-summary">
            <span className="rounded-full bg-[#4A1C17] text-[#F7EBDF] px-3 py-1 text-xs font-semibold">{counts.partenze} partenze</span>
            <span className="rounded-full bg-[#7C5A4E] text-[#F7EBDF] px-3 py-1 text-xs font-semibold">{counts.arrivi} arrivi</span>
            <span className="rounded-full bg-white border border-[#E8DCC8] text-[#4A1C17] px-3 py-1 text-xs font-semibold">{counts.inCasa} in casa</span>
          </div>
        )}

        <div className="mt-4 space-y-4">
          {data && data.rooms.length === 0 && (
            <p className="text-center text-[#7C5A4E] py-10" data-testid="no-rooms-msg">Nessuna camera configurata. Vai in Impostazioni per aggiungerne una.</p>
          )}
          {data && data.rooms.map((room, i) => (
            <RoomCard key={room.id} room={room} onToggle={toggle} onMark={markFrigo} onGuestSaved={load} onLinenMark={markLinen} index={i} />
          ))}
          {!data && <p className="text-center text-[#7C5A4E] py-10">Caricamento…</p>}
        </div>
      </div>
    </div>
  );
}
